<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">自動回應訊息管理</h1>
      </div>
      <div class="header-actions">
        <button type="button" class="btn btn-primary" @click="showTypeModal = true">
          <i class="ri-add-line"></i>
          新增訊息
        </button>
      </div>
    </div>

    <div class="page-container">
    <!-- 自動回應說明 -->
    <div class="list-page-intro" :class="{ 'is-expanded': introOpen }">
      <button type="button" class="intro-toggle" @click="introOpen = !introOpen">
        <span class="intro-title">
          <i class="ri-information-line"></i>
          自動回應訊息的作用
        </span>
        <span class="intro-toggle-action">
          <span class="intro-toggle-label">{{ introOpen ? '收起' : '查看說明' }}</span>
          <i :class="introOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'"></i>
        </span>
      </button>
      <div v-show="introOpen" class="intro-body">
        <p class="intro-text">當好友在 LINE 官方帳號傳送訊息或點擊訊息按鈕時，依規則自動回覆，無需人工即時在線。</p>
        <ul class="intro-list">
          <li><strong>文字觸發</strong>：好友輸入指定關鍵字時觸發，可設定完全比對或部分比對，並依優先級決定哪條規則優先回覆。</li>
          <li><strong>按鈕觸發</strong>：好友點擊訊息中的 Postback 按鈕時觸發；請在「LINE 訊息範本」編輯按鈕時從清單選擇對應規則。</li>
          <li><strong>回覆內容</strong>：可選用「LINE 訊息範本」或自訂文字／圖片等；匹配成功後也可自動為好友貼上指定標籤。</li>
          <li><strong>提示</strong>：僅「啟用」中的規則會觸發；各規則可另設時段控制，非營業時間將不會回覆。</li>
        </ul>
      </div>
    </div>

    <!-- 類型選擇 Modal -->
    <div v-if="showTypeModal" class="type-modal-overlay" @click.self="showTypeModal = false">
      <div class="type-modal">
        <div class="type-modal-header">
          <span>選擇訊息類型</span>
          <button class="btn-close" @click="showTypeModal = false"><i class="ri-close-line"></i></button>
        </div>
        <div class="type-modal-body">
          <button class="type-card" @click="goCreate('keyword')">
            <i class="ri-chat-3-line"></i>
            <strong>文字觸發</strong>
            <span>用戶輸入關鍵字時自動回覆</span>
          </button>
          <button class="type-card" @click="goCreate('postback')">
            <i class="ri-hand-coin-line"></i>
            <strong>按鈕觸發</strong>
            <span>用戶點擊訊息按鈕時自動回覆</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Tab 切換列 -->
    <div class="tab-bar">
      <button
        class="tab-btn"
        :class="{ active: activeTab === 'keyword' }"
        @click="switchTab('keyword')"
      >
        <i class="ri-chat-3-line"></i>
        文字觸發訊息
        <span class="tab-count" v-if="pagination.total > 0">{{ pagination.total }}</span>
      </button>
      <button
        class="tab-btn"
        :class="{ active: activeTab === 'postback' }"
        @click="switchTab('postback')"
      >
        <i class="ri-hand-coin-line"></i>
        按鈕觸發訊息
        <span class="tab-count" v-if="pbPagination.total > 0">{{ pbPagination.total }}</span>
      </button>
    </div>

    <!-- ===================== Tab: 文字觸發規則 ===================== -->
    <div v-show="activeTab === 'keyword'">
      <div class="tab-hint">用戶輸入關鍵字時自動觸發回覆</div>

      <!-- 搜尋表單 -->
      <div class="page-toolbar">
        <div class="search-box">
          <input
            v-model="searchForm.search"
            type="text"
            class="form-control"
            placeholder="搜尋規則名稱或關鍵字..."
            @input="handleSearch"
          />
          <i class="ri-search-line"></i>
        </div>
        <div class="filter-group">
          <select v-model="searchForm.isActive" class="form-control" @change="handleSearch">
            <option value="">全部狀態</option>
            <option :value="true">啟用</option>
            <option :value="false">停用</option>
          </select>
        </div>
        <button class="btn btn-secondary" @click="handleReset">
          <i class="ri-refresh-line"></i>
          重置
        </button>
      </div>

      <div v-if="loading" class="loading-state">
        <div class="spinner"></div>
        <p>載入中...</p>
      </div>

      <div v-else class="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>規則名稱</th>
              <th>關鍵字</th>
              <th>回覆類型</th>
              <th>優先級</th>
              <th>命中次數</th>
              <th>狀態</th>
              <th>最後觸發</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="rule in rules" :key="rule.id">
              <td>{{ rule.ruleName }}</td>
              <td>
                <div class="keywords-list">
                  <span v-for="(keyword, idx) in rule.keywords" :key="idx" class="keyword-badge">
                    {{ keyword }}
                  </span>
                </div>
              </td>
              <td>
                <span class="badge badge-info">{{ getReplyTypeText(rule.replyType) }}</span>
              </td>
              <td class="text-center">{{ rule.priority }}</td>
              <td class="text-center">{{ rule.hitCount || 0 }}</td>
              <td class="text-center">
                <label class="switch">
                  <input type="checkbox" :checked="rule.isActive" @change="handleToggleActive(rule)" />
                  <span class="slider"></span>
                </label>
              </td>
              <td>{{ rule.lastTriggeredAt ? formatDate(rule.lastTriggeredAt) : '尚未觸發' }}</td>
              <td>
                <div class="action-buttons">
                  <button class="btn btn-sm btn-primary" @click="$router.push({ name: 'AutoReplyRuleEdit', params: { id: rule.id } })">
                    <i class="ri-edit-line"></i> 編輯
                  </button>
                  <button class="btn btn-sm btn-danger" @click="handleDelete(rule)">
                    <i class="ri-delete-bin-line"></i> 刪除
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
        <div v-if="rules.length === 0" class="empty-state"><p>暫無文字觸發規則</p></div>
      </div>

      <ListPagination
        v-if="pagination.totalPages > 1"
        :page="pagination.page"
        :total-pages="pagination.totalPages"
        :total="pagination.total"
        @change="handlePageChange"
      />
    </div>

    <!-- ===================== Tab: 按鈕觸發規則 ===================== -->
    <div v-show="activeTab === 'postback'">
      <div class="tab-hint">
        <i class="ri-information-line"></i>
        用戶點擊訊息中帶有 <strong>Postback</strong> 的按鈕時觸發。請在 <strong>LINE 訊息範本</strong>編輯按鈕時從清單選擇規則，由系統自動綁定。
      </div>

      <!-- 搜尋工具列 -->
      <div class="page-toolbar">
        <div class="search-box">
          <input
            v-model="pbSearch.search"
            type="text"
            class="form-control"
            placeholder="搜尋規則名稱..."
            @input="pbHandleSearch"
          />
          <i class="ri-search-line"></i>
        </div>
        <div class="filter-group">
          <select v-model="pbSearch.isActive" class="form-control" @change="pbHandleSearch">
            <option value="">全部狀態</option>
            <option :value="true">啟用</option>
            <option :value="false">停用</option>
          </select>
        </div>
        <button class="btn btn-secondary" @click="pbHandleReset">
          <i class="ri-refresh-line"></i>
          重置
        </button>
      </div>

      <div v-if="pbLoading" class="loading-state">
        <div class="spinner"></div>
        <p>載入中...</p>
      </div>

      <div v-else class="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>規則名稱</th>
              <th>回覆訊息範本</th>
              <th>命中次數</th>
              <th>優先級</th>
              <th>狀態</th>
              <th>最後觸發</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="rule in pbRules" :key="rule.id">
              <td>{{ rule.ruleName }}</td>
              <td>
                <span v-if="rule.templateName" class="template-badge">
                  <i class="ri-message-3-line"></i>
                  {{ rule.templateName }}
                </span>
                <span v-else class="text-muted">（未設定）</span>
              </td>
              <td class="text-center">{{ rule.hitCount || 0 }}</td>
              <td class="text-center">{{ rule.priority }}</td>
              <td class="text-center">
                <label class="switch">
                  <input type="checkbox" :checked="rule.isActive" @change="pbToggleActive(rule)" />
                  <span class="slider"></span>
                </label>
              </td>
              <td>{{ rule.lastTriggeredAt ? formatDate(rule.lastTriggeredAt) : '尚未觸發' }}</td>
              <td>
                <div class="action-buttons">
                  <button class="btn btn-sm btn-primary" @click="$router.push({ name: 'PostbackRuleEdit', params: { id: rule.id } })">
                    <i class="ri-edit-line"></i> 編輯
                  </button>
                  <button class="btn btn-sm btn-danger" @click="pbHandleDelete(rule)">
                    <i class="ri-delete-bin-line"></i> 刪除
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
        <div v-if="pbRules.length === 0" class="empty-state"><p>暫無按鈕觸發規則</p></div>
      </div>

      <ListPagination
        v-if="pbPagination.totalPages > 1"
        :page="pbPagination.page"
        :total-pages="pbPagination.totalPages"
        :total="pbPagination.total"
        @change="pbChangePage"
      />
    </div>

    </div>
  </div>
</template>

<script>
import { autoReplyService } from '@/api/modules/autoReply.js';
import { postbackRuleService } from '@/api/modules/postbackRules.js';
import { lineMsgTemplateService } from '@/api/modules/lineMsgTemplates.js';
import { showSuccess, showError, showDeleteConfirm } from '@/utils/swal.js';

export default {
  name: 'AutoReplyRules',
  data() {
    return {
      introOpen: false,
      showTypeModal: false,
      activeTab: 'keyword',

      // --- 關鍵字回應 ---
      loading: false,
      rules: [],
      pagination: {
        page: 1,
        pageSize: 20,
        total: 0,
        totalPages: 0
      },
      searchForm: {
        search: '',
        isActive: ''
      },

      // --- Postback 回應 ---
      pbLoading: false,
      pbRules: [],
      pbPagination: { page: 1, pageSize: 20, total: 0, totalPages: 0 },
      pbSearch: { search: '', isActive: '' },
      templateList: []
    };
  },
  mounted() {
    if (this.$route.query.tab === 'postback') {
      this.activeTab = 'postback';
    }
    this.loadRules();
    this.loadTemplates();
    this.pbLoadRules();
  },
  methods: {
    async loadRules() {
      this.loading = true;
      try {
        // 過濾掉空值參數（空字串、null、undefined）
        const params = {
          page: this.pagination.page,
          pageSize: this.pagination.pageSize
        };
        
        // 只添加非空值參數
        if (this.searchForm.search && this.searchForm.search.trim()) {
          params.search = this.searchForm.search.trim();
        }
        
        if (this.searchForm.isActive !== '' && this.searchForm.isActive !== null && this.searchForm.isActive !== undefined) {
          params.isActive = this.searchForm.isActive;
        }
        
        const response = await autoReplyService.listRules(params);
        this.rules = response.data.items || [];
        const pg = response.data.pagination || {};
        this.pagination = {
          ...this.pagination,
          page: pg.page || this.pagination.page,
          pageSize: pg.pageSize || this.pagination.pageSize,
          total: pg.totalItems ?? pg.total ?? 0,
          totalPages: pg.totalPages || 0,
          hasNext: pg.hasNext,
          hasPrev: pg.hasPrev
        };
      } catch (error) {
        console.error('載入規則失敗:', error);
        showError('載入規則失敗', '');
      } finally {
        this.loading = false;
      }
    },

    handleSearch() {
      this.pagination.page = 1;
      this.loadRules();
    },

    handleReset() {
      this.searchForm = {
        search: '',
        isActive: ''
      };
      this.pagination.page = 1;
      this.loadRules();
    },

    handlePageChange(page) {
      this.pagination.page = page;
      this.loadRules();
    },

    async handleDelete(rule) {
      const confirmed = await showDeleteConfirm(
        '確定要刪除規則嗎？',
        `規則「${rule.ruleName}」將被永久刪除，此操作無法復原。`
      );
      if (!confirmed) {
        return;
      }

      try {
        await autoReplyService.deleteRule(rule.id);
        await showSuccess('刪除成功', '');
        this.loadRules();
      } catch (error) {
        console.error('刪除規則失敗:', error);
        showError('刪除規則失敗', '');
      }
    },

    async handleToggleActive(rule) {
      try {
        await autoReplyService.updateRule(rule.id, {
          isActive: !rule.isActive
        });
        await showSuccess('更新成功', '');
        this.loadRules();
      } catch (error) {
        console.error('更新規則失敗:', error);
        showError('更新規則失敗', '');
        // 恢復原狀態
        this.loadRules();
      }
    },

    getReplyTypeText(type) {
      const map = {
        template: '範本',
        text: '文字',
        faq: 'FAQ'
      };
      return map[type] || type;
    },

    formatDate(date) {
      if (!date) return '-';
      return new Date(date).toLocaleString('zh-TW');
    },

    switchTab(tab) {
      this.activeTab = tab;
    },

    goCreate(type) {
      this.showTypeModal = false;
      if (type === 'keyword') {
        this.$router.push({ name: 'AutoReplyRuleCreate' });
      } else {
        this.$router.push({ name: 'PostbackRuleCreate' });
      }
    },

    // ── Postback 方法 ─────────────────────────────────────
    async loadTemplates() {
      try {
        const res = await lineMsgTemplateService.list({ pageSize: 100, status: 'active', audienceType: 'personal' });
        this.templateList = res.data?.items || [];
      } catch (e) {
        console.error('載入訊息範本失敗:', e);
      }
    },

    async pbLoadRules() {
      this.pbLoading = true;
      try {
        const params = { page: this.pbPagination.page, limit: this.pbPagination.pageSize };
        if (this.pbSearch.search) params.search = this.pbSearch.search;
        if (this.pbSearch.isActive !== '') params.isActive = this.pbSearch.isActive;
        const res = await postbackRuleService.list(params);
        this.pbRules = res.data?.items || [];
        const pg = res.data?.pagination || {};
        this.pbPagination = {
          page: pg.page || 1,
          pageSize: pg.pageSize || 20,
          total: pg.totalItems ?? pg.total ?? 0,
          totalPages: pg.totalPages || 0
        };
      } catch (e) {
        console.error('載入 Postback 規則失敗:', e);
        showError('載入 Postback 規則失敗', '');
      } finally {
        this.pbLoading = false;
      }
    },

    pbHandleSearch() {
      this.pbPagination.page = 1;
      this.pbLoadRules();
    },

    pbHandleReset() {
      this.pbSearch = { search: '', isActive: '' };
      this.pbPagination.page = 1;
      this.pbLoadRules();
    },

    pbChangePage(page) {
      this.pbPagination.page = page;
      this.pbLoadRules();
    },

    async pbHandleDelete(rule) {
      const confirmed = await showDeleteConfirm(
        '確定要刪除規則嗎？',
        `Postback 規則「${rule.ruleName}」將被永久刪除，此操作無法復原。`
      );
      if (!confirmed) return;
      try {
        await postbackRuleService.remove(rule.id);
        await showSuccess('刪除成功', '');
        this.pbLoadRules();
      } catch (e) {
        showError('刪除失敗', '');
      }
    },

    async pbToggleActive(rule) {
      try {
        await postbackRuleService.update(rule.id, { isActive: !rule.isActive });
        this.pbLoadRules();
      } catch (e) {
        showError('更新失敗', '');
        this.pbLoadRules();
      }
    },

    msgTypeLabel(type) {
      const map = {
        text: '文字', image: '圖片', video: '影片',
        flex: 'Flex', flex_carousel: 'Flex 輪播',
        image_carousel: '圖片輪播', imagemap: '圖文訊息'
      };
      return map[type] || type;
    }
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

/* Tab 列 */
.tab-bar {
  display: flex;
  gap: 4px;
  border-bottom: 2px solid #e5e7eb;
  margin-bottom: 20px;
}

.tab-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 18px;
  border: none;
  border-bottom: 2px solid transparent;
  margin-bottom: -2px;
  background: none;
  font-size: 14px;
  font-weight: 500;
  color: #6b7280;
  cursor: pointer;
  border-radius: 6px 6px 0 0;
  transition: all .15s;
}

.tab-btn i {
  font-size: 16px;
}

.tab-btn:hover {
  color: #374151;
  background: #f9fafb;
}

.tab-btn.active {
  color: #3b82f6;
  border-bottom-color: #3b82f6;
  background: #eff6ff;
}

.tab-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  background: #e5e7eb;
  color: #374151;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
}

.tab-btn.active .tab-count {
  background: #bfdbfe;
  color: #1d4ed8;
}

.tab-hint {
  font-size: 13px;
  color: #6b7280;
  margin-bottom: 14px;
  display: flex;
  align-items: flex-start;
  gap: 6px;
  line-height: 1.6;
}

.tab-hint i {
  font-size: 15px;
  flex-shrink: 0;
  margin-top: 1px;
  color: #3b82f6;
}

.tab-hint code {
  background: #f3f4f6;
  padding: 1px 5px;
  border-radius: 3px;
  font-size: 12px;
}

/* 類型選擇 Modal */
.type-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.type-modal {
  background: #fff;
  border-radius: 14px;
  width: 480px;
  max-width: 92vw;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  overflow: hidden;
}

.type-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 20px;
  font-size: 15px;
  font-weight: 600;
  color: #111827;
  border-bottom: 1px solid #f3f4f6;
}

.type-modal-header .btn-close {
  background: none;
  border: none;
  font-size: 20px;
  color: #9ca3af;
  cursor: pointer;
  padding: 2px;
  line-height: 1;
  display: flex;
  align-items: center;
}
.type-modal-header .btn-close:hover { color: #374151; }

.type-modal-body {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  padding: 24px;
}

.type-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 28px 16px;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  background: #fafafa;
  cursor: pointer;
  transition: all 0.2s;
  text-align: center;
}
.type-card:hover {
  border-color: #3b82f6;
  background: #eff6ff;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);
}
.type-card i {
  font-size: 32px;
  color: #3b82f6;
}
.type-card strong {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
}
.type-card span {
  font-size: 12px;
  color: #6b7280;
  line-height: 1.4;
}

/* Postback 說明框 */
.postback-hint {
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  padding: 12px 16px;
  font-size: 13px;
  color: #1e40af;
  margin-bottom: 16px;
  display: flex;
  gap: 8px;
  align-items: flex-start;
  line-height: 1.6;
}

.postback-hint i {
  font-size: 16px;
  flex-shrink: 0;
  margin-top: 1px;
}

.postback-hint code {
  background: #dbeafe;
  padding: 1px 5px;
  border-radius: 3px;
  font-size: 12px;
}

.template-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  background: #d1fae5;
  color: #065f46;
  border-radius: 4px;
  font-size: 12px;
}

/* form-row */
.form-row {
  display: flex;
  gap: 16px;
}

.form-group-half {
  flex: 1;
}

.text-muted {
  color: #9ca3af;
  font-size: 13px;
}

.keywords-list {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.keyword-badge {
  display: inline-block;
  padding: 2px 8px;
  background-color: #e3f2fd;
  color: #1976d2;
  border-radius: 4px;
  font-size: 12px;
}

.keywords-input {
  margin-bottom: 8px;
}

.keyword-input-item {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
}

.keyword-input-item .form-control {
  flex: 1;
}

.time-range-settings {
  padding: 12px;
  background-color: #f5f5f5;
  border-radius: 4px;
  margin-top: 8px;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #ddd;
}

.action-buttons {
  display: flex;
  gap: 4px;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-dialog {
  background: white;
  border-radius: 8px;
  max-width: 800px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-lg {
  max-width: 1000px;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #ddd;
}

.modal-header h3 {
  margin: 0;
}

.btn-close {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-body {
  padding: 20px;
}

.form-section {
  margin-bottom: 24px;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 2px solid #e0e0e0;
}

.form-group {
  margin-bottom: 16px;
}

.form-group label {
  display: block;
  margin-bottom: 6px;
  font-weight: 500;
}

.form-group label.required::after {
  content: ' *';
  color: #f44336;
}

.form-text {
  display: block;
  margin-top: 4px;
  font-size: 12px;
  color: #666;
}

.switch {
  position: relative;
  display: inline-block;
  width: 50px;
  height: 24px;
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: 0.4s;
  border-radius: 24px;
}

.slider:before {
  position: absolute;
  content: '';
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  transition: 0.4s;
  border-radius: 50%;
}

input:checked + .slider {
  background-color: #2196f3;
}

input:checked + .slider:before {
  transform: translateX(26px);
}
</style>
